import { Box, Stack, alpha } from '@mui/material';
import { Settings } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';
import { useMediaApplicationContext } from '../../../../../../components/MediaApplicationContext';
import { catalogBannerAspectRatioPadding } from '../../../../../../components/useMediaStyles';
import Loading from '../../../../../../components/v2/ui/Loading/Loading';
import MediaRenderer from '../../../../../media/MediaRenderer';
import { Catalog } from '../../../../catalog.types';
import { hasCatalogLiveShopVideo } from '../../../../products/CatalogScreen/catalogLiveShop.utils';
import { CatalogStatus, getCatalogStatus } from '../../utils/getCatalogStatus';
import CatalogActions, { CatalogActionsProps } from './components/CatalogActions';
import CatalogCardName from './components/CatalogCardName';
import CatalogCardTags from './components/CatalogCardTags';
import { CatalogCardTagType } from './components/types/catalogCardTags.types';

const useStyles = makeStyles()((theme) => ({
  mediaContainer: {
    position: 'relative',
    display: 'flex',
    flex: '1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  media: {
    borderRadius: theme.shape.borderRadiusXl,
  },
  emptyMedia: {
    backgroundColor: theme.palette.secondary.light,
  },
  settingsCoverIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    pointerEvents: 'none',
    '& .lucide': { strokeWidth: 1 },
  },
  activeSettingsCoverIcon: {
    color: theme.palette.text.disabled,
  },
  uploadingCoverOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadiusXl,
    backgroundColor: alpha(theme.palette.common.black, 0.4),
  },
  contentContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0.5),
  },
}));

type CatalogCardBaseProps = {
  catalog: Catalog;
  actions?: CatalogActionsProps[];
  isUploadingCover?: boolean;
};

const getLiveShopTag = (isLive: boolean): CatalogCardTagType =>
  isLive ? CatalogCardTagType.LIVE_SHOP_ON_AIR : CatalogCardTagType.LIVE_SHOP_VIDEO;

const CatalogCardBase = ({ catalog, actions, isUploadingCover }: CatalogCardBaseProps) => {
  const mediaContext = useMediaApplicationContext();
  const catalogStatus = getCatalogStatus(catalog);
  const hasLiveShopVideo = hasCatalogLiveShopVideo(catalog?.liveLink);
  const { classes, cx } = useStyles();

  if (!catalog) {
    return null;
  }

  const getCatalogTags = () => {
    const tags = [];
    if (catalogStatus === CatalogStatus.INACTIVE) {
      tags.push(CatalogCardTagType.INACTIVE);
    } else if (catalogStatus === CatalogStatus.NOT_IN_VALIDITY_DATE) {
      tags.push(CatalogCardTagType.NOT_IN_VALIDITY_DATE);
    }
    if (hasLiveShopVideo) {
      tags.push(getLiveShopTag(catalog.isLiveShop));
    }
    if (catalog.isDynamic) {
      tags.push(CatalogCardTagType.DYNAMIC);
    }

    return tags;
  };

  return (
    <div>
      <Box className={classes.mediaContainer}>
        <MediaRenderer
          autoPlay
          type="IMAGE"
          isDisabled={catalogStatus !== 'ACTIVE'}
          aspectRatio={catalogBannerAspectRatioPadding}
          src={catalog.imageUrl ? mediaContext.getMediaFromSize(catalog.imageUrl, 'sm') : undefined}
          className={cx(classes.media, !catalog.imageUrl && classes.emptyMedia)}
          hideEmptyIcon={catalog.isDefault}
        />

        {catalog.isDefault && !catalog.imageUrl && (
          <Box
            className={cx(
              classes.settingsCoverIcon,
              catalogStatus === 'ACTIVE' && classes.activeSettingsCoverIcon,
            )}
          >
            <Settings size={48} />
          </Box>
        )}

        <CatalogCardTags tags={getCatalogTags()} />

        {isUploadingCover && (
          <Box className={classes.uploadingCoverOverlay}>
            <Loading size={24} sx={{ color: 'common.white' }} />
          </Box>
        )}
      </Box>

      <Stack className={classes.contentContainer}>
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <CatalogCardName name={catalog.name} />

          {actions && <CatalogActions actions={actions} />}
        </Stack>
      </Stack>
    </div>
  );
};

export default CatalogCardBase;
